/**
 * Grouping for the School Pay page.
 *
 * The mirror of utils/billing.js: that groups commission owed BY merchants,
 * this groups the share owed TO schools. Both recompute from transactions on
 * every load rather than trusting a stored figure, so the two pages can never
 * disagree about the same month.
 */

import { getSchoolRevenue } from './transactionRevenue';
import { isReceivedTxn } from './funnel';
// One definition of which month a transaction falls in, shared with Merchant
// Invoices. This page used to prefer `created_at` while invoices preferred
// `auth_at`, so a purchase authorised on the 31st but ingested on the 1st
// landed in a different month on each page — exactly the disagreement the
// docstring above promises cannot happen.
import { monthKeyFromTxn as purchaseMonthKey, monthLabel } from './billing';

export { monthLabel };

/**
 * The month a transaction belongs to on a school statement.
 *
 * Not the purchase month. A school is paid out of money we hold, so a June
 * purchase whose commission Wildfire only paid in August belongs on the August
 * statement — otherwise August's statement would be short and June's would have
 * promised money we did not have. `received_at` is when the vendor's own status
 * first said the money was ours; the purchase month is the fallback for sources
 * with no vendor lifecycle, where the two are the same moment.
 */
export const monthKeyFromTxn = (t) => {
  const received = t && t.received_at;
  if (received) {
    const d = new Date(received);
    if (!Number.isNaN(d.getTime())) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }
  }
  return purchaseMonthKey(t);
};

const schoolOf = (t) => {
  const s = t.school;
  if (!s) return null;
  // The superuser transaction feed serialises school as an object; other
  // callers send a bare id.
  if (typeof s === 'object') return { id: String(s.id), name: s.name || 'Unknown school' };
  return { id: String(s), name: 'Unknown school' };
};

/**
 * [{ key, label, schools: [{ schoolId, name, txnCount, totalSales, totalShare }] }]
 * newest month first, biggest share first within a month.
 */
export const groupSchoolPayByMonth = (transactions, schoolsById = {}) => {
  const byMonth = new Map();

  for (const t of transactions || []) {
    // Only money we actually hold. A school cannot be paid a share of a
    // commission the vendor has not remitted, so anything still `pending` with
    // Olive or Wildfire is out — as is anything failed, which `isReceivedTxn`
    // already excludes.
    if (!isReceivedTxn(t)) continue;
    const share = getSchoolRevenue(t);
    if (!share) continue;
    const school = schoolOf(t);
    if (!school) continue;
    const mKey = monthKeyFromTxn(t);
    if (!mKey) continue;

    if (!byMonth.has(mKey)) byMonth.set(mKey, new Map());
    const schools = byMonth.get(mKey);
    if (!schools.has(school.id)) {
      schools.set(school.id, {
        schoolId: school.id,
        name: schoolsById[school.id]?.name || school.name,
        txnCount: 0,
        totalSales: 0,
        totalShare: 0,
      });
    }
    const row = schools.get(school.id);
    row.txnCount += 1;
    row.totalSales += Number(t.amount || 0);
    row.totalShare += share;
  }

  return Array.from(byMonth.entries())
    .map(([key, schools]) => ({
      key,
      label: monthLabel(key),
      schools: Array.from(schools.values()).sort((a, b) => b.totalShare - a.totalShare),
    }))
    .sort((a, b) => (a.key < b.key ? 1 : -1));
};

/** lookup(schoolId, period) -> the statement for that cell, or null. */
export const buildSentIndex = (overview) => {
  const map = new Map();
  for (const st of overview?.statements || []) {
    map.set(`${st.school}::${st.period}`, st);
  }
  return { lookup: (schoolId, period) => map.get(`${schoolId}::${period}`) || null };
};
